import type { IconType } from "react-icons";
import { FiFileText, FiList, FiPlus, FiSearch, FiZap } from "react-icons/fi";
import type { AppRole } from "../../../common/layout/v2/navConfig";
import type { SubAccess } from "../../../common/subscription/useSub";
import { urls } from "../../../urls";

export type HeroActionTarget = { kind: "navigate"; to: string } | { kind: "action"; action: "createSourcingEvent" | "createNeed" };
export type HeroMetric = "reportedNeeds" | "activeContracts";
export type PanelKind =
  | "sourcingEvents"
  | "myNeeds"
  | "activeContracts"
  | "invitations"
  | "myBids"
  | "openProcurements"
  | "upcomingDeadlines"
  | "contractExpiry"
  | "procurementPhases"
  | "recentContracts";

export interface HeroAction {
  key: string;
  labelKey: string;
  sublabelKey: string;        // static fallback text; metric overrides it when set
  icon: IconType;
  variant: "primary" | "default";
  target: HeroActionTarget;
  metric?: HeroMetric;
  /** Gate the action on a `useSub()` flag (role-based view access). */
  access?: keyof SubAccess;
}
export interface HomeConfig {
  headlineKey: string;
  heroActions: HeroAction[];
  panels: PanelKind[];
}

/**
 * The domain each panel belongs to, as a `useSub()` gate. A viewer role only sees the panels of its
 * own domain (admins and owners hold every view role); panels without an entry are ungated.
 */
export const panelAccess: Partial<Record<PanelKind, keyof SubAccess>> = {
  sourcingEvents: "canViewSourcingEvents",
  upcomingDeadlines: "canViewSourcingEvents",
  procurementPhases: "canViewSourcingEvents",
  myNeeds: "canReportNeeds",
  activeContracts: "canViewContracts",
  contractExpiry: "canViewContracts",
  recentContracts: "canViewContracts",
};

export const filterHomeConfig = (config: HomeConfig, sub: Partial<SubAccess>): HomeConfig => ({
  ...config,
  heroActions: config.heroActions.filter((action) => !action.access || sub[action.access]),
  panels: config.panels.filter((panel) => {
    const access = panelAccess[panel];
    return !access || sub[access];
  }),
});

const CHART_PANELS = ["contractExpiry", "procurementPhases"] as const satisfies readonly PanelKind[];

export const homeConfig: Record<AppRole, HomeConfig> = {
  advisor: {
    headlineKey: "What will you work on today?",
    heroActions: [
      { key: "start", labelKey: "Start procurement", sublabelKey: "Create and announce", icon: FiPlus, variant: "primary", target: { kind: "action", action: "createSourcingEvent" }, access: "canCreateEvents" },
      { key: "assess", labelKey: "Assess need", sublabelKey: "Review reported needs", icon: FiZap, variant: "default", target: { kind: "navigate", to: `${urls.v2.needs.list}?status=REPORTED` }, metric: "reportedNeeds", access: "canAssessNeeds" },
      { key: "agreements", labelKey: "Active agreements", sublabelKey: "Your live contracts", icon: FiFileText, variant: "default", target: { kind: "navigate", to: urls.v2.contracts.active }, metric: "activeContracts", access: "canViewContracts" },
    ],
    panels: [...CHART_PANELS, "upcomingDeadlines", "sourcingEvents", "recentContracts"],
  },
  orderer: {
    headlineKey: "What are you looking for?",
    heroActions: [
      { key: "report", labelKey: "Report need", sublabelKey: "Register a new need", icon: FiPlus, variant: "primary", target: { kind: "action", action: "createNeed" }, access: "canReportNeeds" },
      { key: "mine", labelKey: "My needs", sublabelKey: "Your registered needs", icon: FiList, variant: "default", target: { kind: "navigate", to: urls.v2.needs.mine }, access: "canReportNeeds" },
      { key: "agreements", labelKey: "Active agreements", sublabelKey: "Your live contracts", icon: FiFileText, variant: "default", target: { kind: "navigate", to: urls.v2.contracts.active }, metric: "activeContracts", access: "canViewContracts" },
    ],
    // The sourcing panels only appear for orderers whose roles include sourcing view access — a
    // sourcing viewer sees the whole procurement picture, just without the create/edit entries.
    panels: [
      ...CHART_PANELS,
      "myNeeds",
      "sourcingEvents",
      "upcomingDeadlines",
      "activeContracts",
      "recentContracts",
    ],
  },
  supplier: {
    headlineKey: "Find your next opportunity",
    heroActions: [
      { key: "browse", labelKey: "Find procurements", sublabelKey: "Browse published competitions", icon: FiSearch, variant: "primary", target: { kind: "navigate", to: urls.v2.supplier.procurements } },
      { key: "bids", labelKey: "My bids", sublabelKey: "Your offers in progress", icon: FiFileText, variant: "default", target: { kind: "navigate", to: urls.v2.supplier.bids } },
      { key: "invitations", labelKey: "My invitations", sublabelKey: "Invitations to submit a bid", icon: FiList, variant: "default", target: { kind: "navigate", to: urls.v2.supplier.invitations } },
    ],
    panels: ["invitations", "myBids", "openProcurements"],
  },
};
